import React, { type FC } from 'react';

import {
    CompactCalendar,
    FooterLayout,
    ScrollableCalendar,
    type CalendarProps,
} from '@eventbrite/timed-entry';

import { InfiniteCalendarSeriesDispatcher } from '../../../actions/infiniteCalendarSeries';
import { isCompactCalendar } from '../../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import {
    CalendarType,
    DateEventStatus,
    type DateAvailabilityStatus,
    type InfiniteCalendarSeriesNamespace,
} from '../../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import defaultScrollableStyles from './CalendarContainer.module.scss';
import rebrandScrollableStyles from './CalendarContainer.rebranding.module.scss';
import { getCalendarDateCellProps, getFooterSignals } from './CalendarProps';
import defaultCompactCalendarStyles from './CompactCalendar.module.scss';
import rebrandingCompactCalendarStyles from './CompactCalendar.rebranding.module.scss';
import { trimISODatetimeToDate } from './utils';

const compactStylesModules = {
    [DEFAULT_STYLES]: defaultCompactCalendarStyles,
    [REBRANDING_STYLES]: rebrandingCompactCalendarStyles,
};

const scrollableStylesModules = {
    [DEFAULT_STYLES]: defaultScrollableStyles,
    [REBRANDING_STYLES]: rebrandScrollableStyles,
};

type CalendarContainerProps = Required<
    Pick<
        CalendarProps<DateAvailabilityStatus>,
        | 'firstMonth'
        | 'initialSelectedDate'
        | 'lastMonth'
        | 'locale'
        | 'onMonthLoad'
    >
> &
    Pick<
        InfiniteCalendarSeriesNamespace,
        | 'calendarType'
        | 'compactCalendarData'
        | 'isAppRebrandActive'
        | 'scrollCalendarData'
    > &
    Pick<InfiniteCalendarSeriesDispatcher, 'onDateSelect'>;

export const CalendarContainer: FC<CalendarContainerProps> = ({
    calendarType,
    scrollCalendarData,
    compactCalendarData,
    initialSelectedDate,
    locale,
    firstMonth,
    lastMonth,
    onDateSelect,
    onMonthLoad,
    isAppRebrandActive,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const compactCalendarStyles = compactStylesModules[theme];
    const scrollableStyles = scrollableStylesModules[theme];
    const styles =
        calendarType === CalendarType.scrollCalendar
            ? scrollableStyles
            : compactCalendarStyles;
    const calendar =
        calendarType === CalendarType.scrollCalendar ? (
            <ScrollableCalendar
                calendarData={scrollCalendarData}
                dateCellProps={getCalendarDateCellProps(isAppRebrandActive)}
                initialSelectedDate={initialSelectedDate}
                locale={locale}
                firstMonth={firstMonth}
                lastMonth={lastMonth}
                onMonthLoad={onMonthLoad}
                onDateSelect={(dateString) => {
                    onDateSelect(dateString, calendarType);
                }}
                defaultDateCellKey={DateEventStatus.unavailable}
                monthNameClassName={styles.monthName}
                weekdayNameClassName={styles.weekday}
                dateTextClassName={styles.dateText}
                enabledDateCellClassName={styles.enabledDateCell}
                selectedDateCellClassName={styles.selectedDateCell}
                selectedDateCellIconClassName={styles.selectedDateCellIcon}
                todayDateCellClassName={styles.todayDateCell}
            />
        ) : isCompactCalendar(calendarType) ? (
            <div className={styles.compactCalendarContainer}>
                <CompactCalendar
                    calendarData={compactCalendarData}
                    initialSelectedDate={trimISODatetimeToDate(
                        initialSelectedDate,
                    )}
                    isLoading={false}
                    locale={locale}
                    onDateSelect={(dateString, id, sessionTime) => {
                        onDateSelect(dateString, calendarType, id, sessionTime);
                    }}
                    selectedClassName={styles.selectedDateCell}
                    dateTextClassName={styles.dateText}
                />
            </div>
        ) : null;

    return (
        <>
            {calendar}
            <div className={styles.footerLayout}>
                <FooterLayout
                    footerMetaData={getFooterSignals(isAppRebrandActive)}
                />
            </div>
        </>
    );
};
